import { printHTML, downloadWord } from '../utils/printHelper';
import { useEffect, useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { formatDatePrint } from '../utils/dateFormat';
import { DateInput } from '../components/DateInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Plus, Pencil, Trash, Printer, FileDoc, ForkKnife } from '@phosphor-icons/react';
import { confirmDialog } from '@/components/ConfirmDialog';
import ClientAutocomplete from '../components/ClientAutocomplete';
import { DZ_BAR_LOGO_DATA_URL } from '../assets/dzBarLogo';

export default function HranaPage() {
  const [zahtjevi, setZahtjevi] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const emptyForm = {
    vrsta_zahtjeva: 'hrana',
    client_id: '', objekat_naziv_adresa: '', broj_uzoraka: 0,
    hrana_vrsta_analize: 'mikrobioloska', // mikrobioloska | fizicko_hemijska | obje
    podnosilac_naziv: '', podnosilac_adresa: '', podnosilac_pib: '',
    podnosilac_pdv: '', podnosilac_kontakt: '', nacin_preuzimanja: 'licno',
    mjesto_podnosenja: '', datum_podnosenja: new Date().toISOString().split('T')[0],
    kontakt_osoba: '', kontakt_telefon: '',
    metode_uzorkovanja_prihvata: true, metode_uzorkovanja_posebni: '',
    metode_ispitivanja_prihvata: true, metode_ispitivanja_posebni: '',
    metode_ispitivanja_hemija_prihvata: true, metode_ispitivanja_hemija_posebni: '',
    izjava_usaglasenosti: false, izjava_specifikacija: '',
    pravilo_odlucivanja: 'pravilo_1', saglasnost_eksterna: true, ovlasceno_lice: '', napomena: '',
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const [z, c] = await Promise.all([api.get('/zahtjevi?vrsta=hrana'), api.get('/clients')]);
      setZahtjevi(z.data); setClients(c.data);
    } catch { toast.error('Greška pri učitavanju'); }
    finally { setLoading(false); }
  };

  const handleClientSelect = (clientId) => {
    const c = clients.find(cl => cl.id === clientId);
    if (c) setForm(prev => ({
      ...prev, client_id: clientId, podnosilac_naziv: c.naziv_firme || c.skraceni_naziv,
      objekat_naziv_adresa: `${c.naziv_firme || c.skraceni_naziv || ''}${c.adresa ? ', ' + c.adresa : ''}${c.grad ? ', ' + c.grad : ''}`,
      podnosilac_adresa: [c.adresa, c.grad].filter(Boolean).join(', '),
      podnosilac_pib: c.pib || '', podnosilac_pdv: c.pdv_status === 'obveznik' ? `ME${c.pib}` : '',
      podnosilac_kontakt: c.telefon || '', kontakt_osoba: c.direktor || '', kontakt_telefon: c.telefon || '',
      ovlasceno_lice: c.direktor || '',
    }));
  };

  // Auto-fill when editing with existing client
  useEffect(() => {
    if (isFormOpen && form.client_id && clients.length > 0 && !form.podnosilac_naziv) {
      handleClientSelect(form.client_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormOpen, clients]);

  const handleSubmit = async () => {
    if (!form.objekat_naziv_adresa || !form.podnosilac_naziv) {
      toast.error('Unesite podatke o objektu i podnosiocu'); return;
    }
    try {
      if (editingId) { await api.put(`/zahtjevi/${editingId}`, form); toast.success('Zahtjev ažuriran'); }
      else { await api.post('/zahtjevi', form); toast.success('Zahtjev kreiran'); }
      setIsFormOpen(false); setEditingId(null); setForm(emptyForm); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Greška'); }
  };

  const handleEdit = (z) => { setEditingId(z.id); setForm({...emptyForm, ...z, vrsta_zahtjeva: 'hrana'}); setIsFormOpen(true); };
  const handleDelete = async (id) => {
    if (!(await confirmDialog('Obrisati zahtjev?'))) return;
    try { await api.delete(`/zahtjevi/${id}`); toast.success('Obrisano'); fetchData(); } catch { toast.error('Greška'); }
  };

  const buildHtml = (z) => {
    const cb = (v) => v ? '&#9746;' : '&#9744;';
    const logoUrl = DZ_BAR_LOGO_DATA_URL;
    const mikro = z.hrana_vrsta_analize === 'mikrobioloska' || z.hrana_vrsta_analize === 'obje';
    const hemija = z.hrana_vrsta_analize === 'fizicko_hemijska' || z.hrana_vrsta_analize === 'obje';
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>HRANA Zahtjev</title>
<style>
@page{size:A4;margin:10mm 15mm 10mm 15mm}
@media print{html,body{-webkit-print-color-adjust:exact}}
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Times New Roman',serif;font-size:12pt;color:#000;line-height:1.4;background:#fff}
table{border-collapse:collapse}
.htbl{width:100%;border:1.5px solid #000;margin-bottom:6px}
.htbl td{border:1px solid #000;padding:4px 6px;vertical-align:middle}
.htbl .logo-cell{width:90px;text-align:center;padding:4px}
.htbl .logo-cell img{width:78px;height:auto}
.htbl .info-cell{text-align:center;font-size:11pt;line-height:1.35}
.htbl .info-cell .title{font-weight:bold;font-size:13pt;margin-top:3px}
.htbl .meta-cell{width:95px;font-size:10pt;text-align:center}
.ftbl{width:100%;border-collapse:collapse;margin-top:2px}
.ftbl td{border:1px solid #000;padding:5px 7px;font-size:11.5pt;vertical-align:top;min-height:22px}
.ftbl .lbl{font-weight:bold;white-space:nowrap}
.sect{font-weight:bold;font-size:12pt;margin:8px 0 2px 0;text-decoration:underline}
.note{font-size:10.5pt;margin:5px 0;line-height:1.35;text-align:justify}
.note-bold{font-weight:bold;font-size:10.5pt;margin:5px 0;line-height:1.35}
.cb-row{margin:3px 0 3px 12px;font-size:11.5pt}
.tbl{width:100%;border-collapse:collapse;margin:4px 0;font-size:11pt}
.tbl td{border:1px solid #000;padding:4px 6px;vertical-align:top}
.sign-row{display:flex;align-items:baseline;margin-top:15px;font-size:12pt;gap:10px}
.page-break{page-break-before:always}
.version{font-size:9pt;margin-top:8px}
.meta-print{margin-top:5px;font-size:8pt;color:#555}
@media print{.meta-print{display:none}}
</style></head><body>

<!-- STRANA 1 -->
<table class="htbl">
<tr>
  <td class="logo-cell" rowspan="2"><img src="${logoUrl}" alt="DZ Bar Logo"></td>
  <td class="info-cell" rowspan="2">
    <strong>ZU &nbsp;Dom zdravlja - Bar</strong><br>
    <strong>ODSJEK ZA HIGIJENU I ZDRAVSTVENU EKOLOGIJU</strong><br>
    Tel: 030/314-885 &nbsp;&nbsp; Tel/fax: 030/311-001<br>
    e-mail: higijenadzbar@gmail.com<br>
    Br.žiro računa: 530-2470-72<br>
    <div class="title">ZAHTJEV ZA UZORKOVANJE I ISPITIVANJE HRANE</div>
  </td>
  <td class="meta-cell" style="border-bottom:1px solid #000"><strong>Oznaka</strong><br>Q4.7.1.00.04</td>
</tr>
<tr><td class="meta-cell"><strong>Str./ukupno<br>str.</strong><br>1/3</td></tr>
</table>

<div style="font-size:11.5pt;margin:6px 0 4px 0;text-align:justify">Molim Vas da obezbijedite ekipu radi uzimanja uzoraka hrane:<br>
<span style="margin-left:8px">a) pakovani proizvodi, &nbsp; b) proizvodi iz frižidera, &nbsp; c) proizvodi na ambijentalnoj temperaturi,</span><br>
<span style="margin-left:8px">d) vrući proizvodi, &nbsp; e) potrošačke porcije iz restorana &nbsp; u objektu:</span></div>

<table class="ftbl">
<tr><td colspan="4"><span class="lbl">Podaci o objektu (navesti tačan naziv i adresu):</span><br>${z.objekat_naziv_adresa||''}</td></tr>
<tr><td colspan="4"><span class="lbl">Broj uzoraka koji se uzima:</span> ${z.broj_uzoraka||''}</td></tr>
</table>

<div style="font-weight:bold;font-size:11.5pt;margin:6px 0 2px 0">Na sledeća ispitivanja:</div>
<div style="margin:2px 0 2px 12px;font-size:11pt;line-height:1.35">${cb(mikro)} <strong>a)</strong> mikrobiološku analizu i to: <em>Salmonella, Listeria monocytogenes, Enterobacteriaceae, Escherichia coli, Koagulaza pozitivne bakterije i aerobne mezofilne bakterije.</em></div>
<div style="margin:2px 0 2px 12px;font-size:11pt">${cb(hemija)} <strong>b)</strong> fizičko-hemijsku analizu</div>

<div style="font-weight:bold;font-size:11.5pt;margin:6px 0 2px 0">Podaci o podnosiocu zahtjeva: Naziv (institucija, firma, pravno/fizičko lice i sl.):</div>
<table class="ftbl">
<tr><td colspan="4">${z.podnosilac_naziv||''}</td></tr>
<tr><td colspan="4"><span class="lbl">Adresa:</span> ${z.podnosilac_adresa||''}</td></tr>
<tr><td colspan="2"><span class="lbl">PIB:</span> ${z.podnosilac_pib||''}</td><td colspan="2"><span class="lbl">PDV:</span> ${z.podnosilac_pdv||''}</td></tr>
<tr><td colspan="4"><span class="lbl">Kontakt tel:</span> ${z.podnosilac_kontakt||''}</td></tr>
</table>

<div class="note-bold" style="margin-top:6px">NAPOMENA: IZVJEŠTAJI SE PREUZIMAJU NAKON 7 DANA OD DANA UZORKOVANJA, UZ DOKAZ O IZVRŠENOJ UPLATI. Cijena uzorkovanja i analize zavise od broja ispitivanih parametara.</div>

<div style="font-size:11.5pt;margin:6px 0 3px 0;font-weight:bold">Označiti način preuzimanja izvještaja:</div>
<div style="margin:2px 0 2px 10px;font-size:11.5pt">${cb(z.nacin_preuzimanja==='licno')} 1) lično u Odsjeku za higijenu i zdravstvenu ekologiju</div>
<div style="margin:2px 0 2px 10px;font-size:11.5pt">${cb(z.nacin_preuzimanja==='postom')} 2) poštom na adresu korisnika navedenu u zahtjevu</div>

<div class="note" style="margin-top:6px">Sve informacije dobijene ili nastale u toku realizacije ugovorenih aktivnosti predstavljaju vlasništvo korisnika i smatraju se povjerljivim. Ukoliko se zakonom zahtjeva da se neke povjerljive informacije otkriju, Odsjek/Laboratorija će učiniti te informacije dostupnim i o tome informisati korisnika, izuzev ako je zakonom zabranjeno.</div>

<div style="font-size:12pt;font-weight:bold;text-align:center;margin:8px 0">ZAHTJEV ISPUNITI ČITKO!</div>

<table class="ftbl">
<tr><td colspan="2"><span class="lbl">Mjesto podnošenja zahtjeva:</span> ${z.mjesto_podnosenja||''}</td><td colspan="2"><span class="lbl">Kontakt osoba:</span> ${z.kontakt_osoba||''}</td></tr>
<tr><td colspan="2"><span class="lbl">Datum:</span> ${formatDatePrint(z.datum_podnosenja)||''} &nbsp;&nbsp; <strong>m.p.</strong></td><td colspan="2"><span class="lbl">Broj telefona:</span> ${z.kontakt_telefon||''}</td></tr>
</table>

<div class="version">Verzija/izmjena: 04/00 &nbsp;&nbsp; datum: 18.09.2025.</div>

<!-- STRANA 2 -->
<div class="page-break"></div>
<table class="htbl">
<tr>
  <td class="logo-cell" rowspan="2"><img src="${logoUrl}" alt="DZ Bar Logo"></td>
  <td class="info-cell" rowspan="2">
    <strong>ZU &nbsp;Dom zdravlja - Bar</strong><br>
    <strong>ODSJEK ZA HIGIJENU I ZDRAVSTVENU EKOLOGIJU</strong><br>
    Tel: 030/314-885 &nbsp;&nbsp; Tel/fax: 030/311-001<br>
    e-mail: higijenadzbar@gmail.com<br>
    Br.žiro računa: 530-2470-72<br>
    <div class="title">ZAHTJEV ZA UZORKOVANJE I ISPITIVANJE HRANE</div>
  </td>
  <td class="meta-cell" style="border-bottom:1px solid #000"><strong>Oznaka</strong><br>Q4.7.1.00.04</td>
</tr>
<tr><td class="meta-cell"><strong>Str./ukupno<br>str.</strong><br>2/3</td></tr>
</table>

<table class="tbl" style="margin-top:8px">
<tr>
  <td style="width:72%"><strong>Metode uzorkovanja</strong></td>
  <td style="text-align:center;width:14%">${cb(z.metode_uzorkovanja_prihvata)} DA</td>
  <td style="text-align:center;width:14%">${cb(!z.metode_uzorkovanja_prihvata)} NE</td>
</tr>
<tr>
  <td><em>Posebni zahtjevi za metode uzorkovanja:</em><br>Navesti koji: ${z.metode_uzorkovanja_posebni||'_____________________________'}</td>
  <td style="text-align:center">${cb(!!z.metode_uzorkovanja_posebni)} DA</td>
  <td style="text-align:center">${cb(!z.metode_uzorkovanja_posebni)} NE</td>
</tr>
<tr>
  <td><strong>Zahtjevane metode ispitivanja Laboratorije sanitarne mikrobiologije</strong></td>
  <td style="text-align:center">${cb(z.metode_ispitivanja_prihvata)} DA</td>
  <td style="text-align:center">${cb(!z.metode_ispitivanja_prihvata)} NE</td>
</tr>
<tr>
  <td><em>Posebni zahtjevi za metode ispitivanja:</em><br>Navesti koji: ${z.metode_ispitivanja_posebni||'_____________________________'}</td>
  <td style="text-align:center">${cb(!!z.metode_ispitivanja_posebni)} DA</td>
  <td style="text-align:center">${cb(!z.metode_ispitivanja_posebni)} NE</td>
</tr>
<tr>
  <td><strong>Zahtjevane metode ispitivanja Laboratorije sanitarne hemije</strong></td>
  <td style="text-align:center">${cb(z.metode_ispitivanja_hemija_prihvata)} DA</td>
  <td style="text-align:center">${cb(!z.metode_ispitivanja_hemija_prihvata)} NE</td>
</tr>
<tr>
  <td><em>Posebni zahtjevi za metode ispitivanja:</em><br>Navesti koji: ${z.metode_ispitivanja_hemija_posebni||'_____________________________'}</td>
  <td style="text-align:center">${cb(!!z.metode_ispitivanja_hemija_posebni)} DA</td>
  <td style="text-align:center">${cb(!z.metode_ispitivanja_hemija_posebni)} NE</td>
</tr>
</table>

<div class="sect">Zahtjev za izjavu o usaglašenosti</div>
<div style="margin:3px 0;font-size:11.5pt">Potrebna izjava o usaglašenosti: &nbsp; ${cb(z.izjava_usaglasenosti)} DA &nbsp; ${cb(!z.izjava_usaglasenosti)} NE</div>
<div style="margin:3px 0;font-size:11.5pt">Ukoliko je potrebna izjava o usaglašenosti navesti prema kojoj specifikaciji (pravilnik, zakon, standard i sl.): <span style="border-bottom:1px solid #000;display:inline-block;min-width:280px;padding:0 3px">${z.izjava_specifikacija||'&nbsp;'}</span><br><em style="font-size:9pt">(popunjava korisnik u slučaju da ima posebne zahtjeve)</em></div>

<div class="sect">Izjava korisnika o Pravilu odlučivanja za ocjenu usaglašenosti uzorka:</div>
<div style="font-size:10pt;margin-bottom:3px"><em>(odabrati i obilježiti jednu od ponuđenih mogućnosti)</em></div>
<div class="note" style="font-size:10pt;margin-bottom:3px">Napomena: Objašnjenja data u Uputstvu Q 3.7.8.00.02 dostupno na zahtjev korisnika usluga</div>
<div class="note">Obaviješteni smo i saglasni sa Pravilom odlučivanja i načinom izvještavanja o rezultatima ispitivanja Laboratorija Doma zdravlja Bar i zahtijevamo da se primjenjuje:</div>
<div class="cb-row">${cb(z.pravilo_odlucivanja==='specifikacija')} Ocjena usaglašenosti u skladu sa pravilom odlučivanja definisanim u specifikaciji;</div>
<div class="cb-row">${cb(z.pravilo_odlucivanja==='pravilo_1')} Pravilo 1: Pravilo podijeljenog rizika</div>
<div class="cb-row">${cb(z.pravilo_odlucivanja==='pravilo_2')} Pravilo 2: Pravilo sigurnog odbijanja rezultata</div>
<div class="cb-row">${cb(z.pravilo_odlucivanja==='pravilo_3')} Pravilo 3: Pravilo sigurnog prihvatanja rezultata</div>

<div class="note-bold" style="margin-top:4px"><strong>NAPOMENA:</strong> U svim slučajevima kada klijent prepusti Laboratoriji Doma zdravlja Bar odluku o tome koje se pravilo odlučivanja primjenjuje, primijeniće se Pravilo 1: Pravilo podijeljenog rizika.${z.napomena ? '<br>'+z.napomena : ''}</div>

<table class="tbl" style="margin-top:14px">
<tr>
  <td style="width:30%;padding:8px 6px">Datum:<br>${formatDatePrint(z.datum_podnosenja)||'&nbsp;'}</td>
  <td style="width:20%;text-align:center;padding:8px 6px">M.P.</td>
  <td style="width:50%;padding:8px 6px">Ovlašćeno lice korisnika usluga<br><span style="border-bottom:1px solid #000;display:inline-block;min-width:200px">${z.ovlasceno_lice||'&nbsp;'}</span></td>
</tr>
</table>

<div class="version">Verzija/izmjena: 04/00 &nbsp;&nbsp; datum: 18.09.2025.</div>

<!-- STRANA 3 -->
<div class="page-break"></div>
<table class="htbl">
<tr>
  <td class="logo-cell" rowspan="2"><img src="${logoUrl}" alt="DZ Bar Logo"></td>
  <td class="info-cell" rowspan="2">
    <strong>ZU &nbsp;Dom zdravlja - Bar</strong><br>
    <strong>ODSJEK ZA HIGIJENU I ZDRAVSTVENU EKOLOGIJU</strong><br>
    Tel: 030/314-885 &nbsp;&nbsp; Tel/fax: 030/311-001<br>
    e-mail: higijenadzbar@gmail.com<br>
    Br.žiro računa: 530-2470-72<br>
    <div class="title">ZAHTJEV ZA UZORKOVANJE I ISPITIVANJE HRANE</div>
  </td>
  <td class="meta-cell" style="border-bottom:1px solid #000"><strong>Oznaka</strong><br>Q4.7.1.00.04</td>
</tr>
<tr><td class="meta-cell"><strong>Str./ukupno<br>str.</strong><br>3/3</td></tr>
</table>

<table class="tbl" style="margin-top:15px">
<tr><td style="padding:15px 12px;text-align:center;line-height:1.5">
  <div style="font-weight:bold;font-size:10.5pt;margin-bottom:8px">IZJAVA &nbsp; KORISNIKA &nbsp; O SAGLASNOSTI ZA EKSTERNO NABAVLJENU USLUGU UZORKOVANJA I ISPITIVANJA</div>
  <div style="font-size:11.5pt">(<u>Popuniti kad je primjenljivo</u>):</div>
  <div style="font-size:11.5pt;margin-top:15px;text-align:justify;padding:0 10px">Upoznati smo i saglasni da se uzorkovanja/ispitivanja koja se ne mogu izvršiti u Laboratorijama Doma zdravlja Bar, izvrše u nekoj od eksterno angažovanih kompetentnih Laboratorija, po izboru Doma zdravlja Bar i uz saglasnost korisnika a čiji su rezultati sastavni dio Izvještaja o ispitivanju Laboratorija Doma zdravlja Bar.</div>
  <div style="display:flex;align-items:flex-end;margin-top:30px;font-size:11.5pt;text-align:left;padding:0 10px">
    <div style="width:30%">Datum:<br><br>${formatDatePrint(z.datum_podnosenja)||'&nbsp;'}</div>
    <div style="width:20%;text-align:center">M.P.</div>
    <div style="width:50%;text-align:center">Ovlašćeno lice korisnika usluga<br><br><span style="border-bottom:1px solid #000;display:inline-block;min-width:200px">${z.ovlasceno_lice||'&nbsp;'}</span></div>
  </div>
</td></tr>
</table>
<div class="version">Verzija/izmjena: 04/00 &nbsp;&nbsp; datum: 18.09.2025.</div>
<div class="meta-print">Kreirao: ${z.created_by_name||'N/A'} | FINANCE GROUP CRM</div>
<script>window.onload=function(){window.print();window.onafterprint=function(){window.close()}}</script></body></html>`;
  };

  const handlePrint = (z) => printHTML(buildHtml(z));
  const handleWord = (z) => downloadWord(buildHtml(z), `HRANA_${(z.podnosilac_naziv||'zahtjev').replace(/\s+/g,'_')}.doc`);

  return (
    <div className="space-y-6" data-testid="hrana-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Zahtjev za uzorkovanje hrane</h3>
          <p className="text-sm text-slate-600 mt-1">Dom zdravlja Bar - Odsjek za higijenu i zdravstvenu ekologiju</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setEditingId(null); setIsFormOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="hrana-add-btn">
          <Plus size={20} weight="bold" className="mr-2" /> Novi zahtjev
        </Button>
      </div>
      {loading ? <p className="text-center text-slate-500 py-16">Učitavanje...</p> : zahtjevi.length === 0 ? (
        <Card className="p-8 text-center"><ForkKnife size={48} weight="duotone" className="text-slate-400 mx-auto mb-4" /><p className="text-slate-500">Nema zahtjeva za uzorkovanje hrane</p></Card>
      ) : (
        <Card className="overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-left text-sm text-slate-600" data-testid="hrana-table"><thead className="bg-slate-50 border-b"><tr>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Datum</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Objekat</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Podnosilac</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Br. uzoraka</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Tip analize</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Formirao</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Status</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Akcije</th>
        </tr></thead><tbody>
          {zahtjevi.map(z => (
            <tr key={z.id} className="border-b hover:bg-slate-50">
              <td className="px-4 py-3 text-xs">{formatDatePrint(z.datum_podnosenja) || formatDatePrint(z.created_at) || '-'}</td>
              <td className="px-4 py-3 text-sm font-medium">{(z.objekat_naziv_adresa||'-').substring(0,40)}</td>
              <td className="px-4 py-3 text-sm">{z.podnosilac_naziv || '-'}</td>
              <td className="px-4 py-3 text-sm text-center">{z.broj_uzoraka || 0}</td>
              <td className="px-4 py-3"><span className="px-2 py-1 rounded text-xs bg-amber-50 text-amber-700 border border-amber-200">{z.hrana_vrsta_analize === 'obje' ? 'Mikro + Hemija' : z.hrana_vrsta_analize === 'fizicko_hemijska' ? 'Fiz.-hemijska' : 'Mikrobiološka'}</span></td>
              <td className="px-4 py-3 text-sm text-slate-600">{z.created_by_name || '-'}</td>
              <td className="px-4 py-3"><span className={`px-2 py-1 rounded text-xs font-medium border ${z.status === 'zavrsen' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : z.status === 'poslan' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>{z.status === 'zavrsen' ? 'Završen' : z.status === 'poslan' ? 'Poslan' : 'Nacrt'}</span></td>
              <td className="px-4 py-3"><div className="flex gap-1">
                <button onClick={() => handlePrint(z)} className="p-2 hover:bg-green-100 rounded" title="Štampaj"><Printer size={16} className="text-green-600" /></button>
                <button onClick={() => handleWord(z)} className="p-2 hover:bg-indigo-100 rounded" title="Word dokument"><FileDoc size={16} className="text-indigo-600" /></button>
                <button onClick={() => handleEdit(z)} className="p-2 hover:bg-slate-200 rounded"><Pencil size={16} className="text-slate-600" /></button>
                <button onClick={() => handleDelete(z.id)} className="p-2 hover:bg-red-100 rounded"><Trash size={16} className="text-red-600" /></button>
              </div></td>
            </tr>
          ))}
        </tbody></table></div></Card>
      )}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? 'Uredi zahtjev' : 'Novi zahtjev - Uzorkovanje hrane'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <Card className="p-3 bg-blue-50 border-blue-200">
              <Label className="text-xs font-semibold uppercase text-blue-700 mb-2 block">Auto-popunjavanje iz klijenta</Label>
              <ClientAutocomplete clients={clients} value={form.client_id} onChange={(id) => ((v) => v !== 'none' && handleClientSelect(v))(id || 'none')} placeholder="Pretraga po nazivu ili PIB-u..." />
            </Card>
            <div><Label className="text-xs font-semibold uppercase text-slate-500">Naziv i adresa objekta *</Label><Input value={form.objekat_naziv_adresa} onChange={e => setForm({...form, objekat_naziv_adresa: e.target.value})} className="bg-white border-slate-300" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold uppercase text-slate-500">Broj uzoraka</Label><Input type="number" value={form.broj_uzoraka} onChange={e => setForm({...form, broj_uzoraka: parseInt(e.target.value)||0})} className="bg-white border-slate-300" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-500">Tip analize</Label>
                <select value={form.hrana_vrsta_analize} onChange={e => setForm({...form, hrana_vrsta_analize: e.target.value})} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                  <option value="mikrobioloska">Mikrobiološka</option>
                  <option value="fizicko_hemijska">Fizičko-hemijska</option>
                  <option value="obje">Mikrobiološka + Fizičko-hemijska</option>
                </select>
              </div>
            </div>
            <h4 className="text-sm font-semibold border-b pb-2">Podnosilac zahtjeva</h4>
            <div><Label className="text-xs font-semibold uppercase text-slate-500">Naziv (institucija, firma, pravno/fizičko lice) *</Label><Input value={form.podnosilac_naziv} onChange={e => setForm({...form, podnosilac_naziv: e.target.value})} className="bg-white border-slate-300" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold uppercase text-slate-500">Adresa</Label><Input value={form.podnosilac_adresa} onChange={e => setForm({...form, podnosilac_adresa: e.target.value})} className="bg-white border-slate-300" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-500">Kontakt tel</Label><Input value={form.podnosilac_kontakt} onChange={e => setForm({...form, podnosilac_kontakt: e.target.value})} className="bg-white border-slate-300" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold uppercase text-slate-500">PIB</Label><Input value={form.podnosilac_pib} onChange={e => setForm({...form, podnosilac_pib: e.target.value})} className="bg-white border-slate-300" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-500">PDV</Label><Input value={form.podnosilac_pdv} onChange={e => setForm({...form, podnosilac_pdv: e.target.value})} className="bg-white border-slate-300" /></div>
            </div>
            <h4 className="text-sm font-semibold border-b pb-2">Način preuzimanja izvještaja</h4>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm"><input type="radio" checked={form.nacin_preuzimanja==='licno'} onChange={()=>setForm({...form,nacin_preuzimanja:'licno'})} className="accent-blue-600" /> Lično u Odsjeku</label>
              <label className="flex items-center gap-2 text-sm"><input type="radio" checked={form.nacin_preuzimanja==='postom'} onChange={()=>setForm({...form,nacin_preuzimanja:'postom'})} className="accent-blue-600" /> Poštom na adresu</label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold uppercase text-slate-500">Mjesto podnošenja</Label><Input value={form.mjesto_podnosenja} onChange={e => setForm({...form, mjesto_podnosenja: e.target.value})} className="bg-white border-slate-300" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-500">Datum</Label><DateInput value={form.datum_podnosenja} onChange={e => setForm({...form, datum_podnosenja: e.target.value})} className="border-slate-300" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold uppercase text-slate-500">Kontakt osoba</Label><Input value={form.kontakt_osoba} onChange={e => setForm({...form, kontakt_osoba: e.target.value})} className="bg-white border-slate-300" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-500">Broj telefona</Label><Input value={form.kontakt_telefon} onChange={e => setForm({...form, kontakt_telefon: e.target.value})} className="bg-white border-slate-300" /></div>
            </div>
            <div><Label className="text-xs font-semibold uppercase text-slate-500">Ovlašćeno lice</Label><Input value={form.ovlasceno_lice} onChange={e => setForm({...form, ovlasceno_lice: e.target.value})} className="bg-white border-slate-300" /></div>
            <div><Label className="text-xs font-semibold uppercase text-slate-500">Napomena</Label><textarea value={form.napomena} onChange={e => setForm({...form, napomena: e.target.value})} className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm min-h-[50px]" /></div>
            <div className="flex gap-3 justify-end pt-4 border-t"><Button variant="outline" onClick={() => setIsFormOpen(false)}>Odustani</Button><Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="hrana-save-btn">{editingId ? 'Ažuriraj' : 'Kreiraj'}</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
